import React, { useRef, useState } from 'react';
import { Camera, Loader2, X, Upload, Star } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImage } from './ImageUpload';
import { useUploadManager } from '../../context/UploadManagerContext';

/**
 * Multi-slot image gallery uploader. Manages up to N images where slot 0 is the
 * primary product image (persisted to `product.image`) and slots 1..N-1 go into
 * the `product.images` array. Each slot can be clicked to upload/replace, or
 * removed with an X button. Also supports drag-and-drop for the first empty
 * slot.
 *
 * Props:
 *  - primary (string)          — current primary image URL
 *  - extras (string[])         — current gallery images (excludes primary)
 *  - onChange({image, images}) — called with the new full state after any change
 *  - max (number)              — total number of slots including primary (default 5)
 */
const ProductImageGallery = ({ primary, extras = [], onChange, max = 5 }) => {
  const slots = [primary, ...extras];
  // Pad to max
  while (slots.length < max) slots.push(null);

  const [busyIdx, setBusyIdx] = useState(-1);
  const [dragging, setDragging] = useState(false);
  const inputRefs = useRef({});
  const mgr = useUploadManager();

  const commit = (nextSlots) => {
    const cleaned = nextSlots.filter(Boolean);
    onChange({
      image: cleaned[0] || 'https://images.unsplash.com/photo-1607870411590-d5e9e06da09a?w=600',
      images: cleaned.slice(1),
    });
  };

  const uploadTo = async (idx, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setBusyIdx(idx);
    try {
      const { promise } = mgr.upload(file, { label: file.name });
      const data = await promise;
      const next = [...slots];
      next[idx] = data.url;
      commit(next);
    } catch {
      /* tray + toast surface the error */
    } finally {
      setBusyIdx(-1);
      const ref = inputRefs.current[idx];
      if (ref) ref.value = '';
    }
  };

  const removeAt = (idx) => {
    const next = [...slots];
    next[idx] = null;
    commit(next);
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const next = [...slots];
    [next[0], next[idx]] = [next[idx], next[0]];
    commit(next);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // find first empty slot; else replace slot 0
    const target = slots.findIndex(s => !s);
    await uploadTo(target === -1 ? 0 : target, file);
  };

  const filledCount = slots.filter(Boolean).length;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-sm p-2 transition-colors ${dragging ? 'border-[#F46A25] bg-orange-50' : 'border-neutral-200 bg-neutral-50'}`}
      data-testid="product-image-gallery"
    >
      <div className="text-[11px] uppercase text-neutral-500 font-semibold mb-1 flex items-center justify-between">
        <span>Images ({filledCount}/{max})</span>
        {dragging && <span className="text-[#F46A25] font-bold">Drop to upload</span>}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {slots.map((src, idx) => {
          const isPrimary = idx === 0;
          const busy = busyIdx === idx;
          return (
            <div key={idx} className="relative group">
              <div
                className={`aspect-square rounded-sm overflow-hidden border ${src ? 'border-neutral-300' : 'border-dashed border-neutral-300 bg-white'} ${isPrimary && src ? 'ring-1 ring-[#F46A25]' : ''}`}
              >
                {src ? (
                  <img src={resolveImage(src)} alt={`Slot ${idx + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <button
                    type="button"
                    onClick={() => inputRefs.current[idx]?.click()}
                    className="w-full h-full flex flex-col items-center justify-center text-neutral-400 hover:text-[#F46A25] hover:bg-orange-50 transition-colors"
                    data-testid={`gallery-slot-empty-${idx}`}
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span className="text-[10px] mt-0.5 font-semibold">{idx + 1}</span>
                  </button>
                )}
                {src && (
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center gap-1 transition-opacity">
                    {busy ? (
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => inputRefs.current[idx]?.click()}
                          className="text-white hover:text-[#F46A25] text-[10px] font-bold uppercase flex items-center gap-1"
                          data-testid={`gallery-slot-replace-${idx}`}
                        >
                          <Camera className="w-3 h-3" /> Replace
                        </button>
                        {!isPrimary && (
                          <button
                            type="button"
                            onClick={() => setPrimary(idx)}
                            className="text-white hover:text-[#F46A25] text-[10px] font-bold uppercase flex items-center gap-1"
                            data-testid={`gallery-slot-primary-${idx}`}
                            title="Make this the primary image"
                          >
                            <Star className="w-3 h-3" /> Primary
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {src && !busy && (
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  aria-label="Remove image"
                  data-testid={`gallery-slot-remove-${idx}`}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-neutral-300 hover:border-red-500 hover:bg-red-500 hover:text-white text-neutral-600 flex items-center justify-center shadow-sm"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              {isPrimary && src && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-[#F46A25] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase whitespace-nowrap">Primary</span>
              )}
              <input
                ref={(el) => { inputRefs.current[idx] = el; }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadTo(idx, e.target.files?.[0])}
              />
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-neutral-500 mt-1.5">Drag &amp; drop or click a slot. Hover any image for actions.</p>
    </div>
  );
};

export default ProductImageGallery;
